import React, { useState, useRef, useEffect } from 'react';
import { useApi } from '../hooks/useApi';

const ImageUpload = ({ ticketId, onImagesUploaded, onClose }) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Camera effect: start/stop stream when showCamera changes
  useEffect(() => {
    if (showCamera && videoRef.current) {
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      }).then(stream => {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }).catch(err => {
        setError('Could not access camera. Please check permissions.');
        console.error('getUserMedia error:', err);
      });
    }
    // Cleanup: stop camera when modal closes
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [showCamera]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedFiles = files.filter(
      file => file.type.startsWith('image/') || file.type === 'application/pdf'
    );
    if (allowedFiles.length !== files.length) {
      setError('Only image files or PDFs are allowed');
      return;
    }
    setSelectedFiles(prev => [...prev, ...allowedFiles]);
    setError('');
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setCapturedImage(file);
        setShowCamera(false);
      }, 'image/jpeg', 0.8);
    }
  };

  const addCapturedImage = () => {
    if (capturedImage) {
      setSelectedFiles(prev => [...prev, capturedImage]);
      setCapturedImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      setError('Please select at least one image');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('images', file);
      });
      if (description) {
        formData.append('description', description);
      }

      const response = await api({
        method: 'post',
        url: `/tickets/${ticketId}/images`,
        data: formData
      });

      if (response.data.success) {
        onImagesUploaded(response.data.data.images);
        onClose();
      } else {
        setError(response.data.message || 'Error uploading images');
      }
    } catch (err) {
      console.error('Error uploading images:', err);
      setError(err.response?.data?.message || 'Error uploading images');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (showCamera) {
    return (
      <div className="modal fade show" style={{ display: 'block', zIndex: 1500 }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Take Photo</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowCamera(false)}
              ></button>
            </div>
            <div className="modal-body text-center">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{ width: '100%', maxWidth: '500px', minHeight: '300px', background: '#000' }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCamera(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={captureImage}
              >
                <i className="bi bi-camera me-2"></i>
                Capture Photo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (capturedImage) {
    return (
      <div className="modal fade show" style={{ display: 'block', zIndex: 1500 }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Review Captured Photo</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setCapturedImage(null)}
              ></button>
            </div>
            <div className="modal-body text-center">
              <img
                src={URL.createObjectURL(capturedImage)}
                alt="Captured"
                style={{ maxWidth: '100%', maxHeight: '400px' }}
                className="img-fluid"
              />
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCapturedImage(null)}
              >
                Retake
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={addCapturedImage}
              >
                Use This Photo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal fade show" style={{ display: 'block', zIndex: 1500 }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Upload Images</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Image Description (Optional)</label>
                <textarea
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="2"
                  placeholder="Describe the images..."
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Select Images</label>
                <div className="d-flex gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className="bi bi-upload me-2"></i>
                    Choose Files
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    onClick={() => setShowCamera(true)}
                  >
                    <i className="bi bi-camera me-2"></i>
                    Take Photo
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <small className="text-muted">
                  Maximum 10 images, 10MB each. Supported formats: JPG, PNG, GIF, WebP
                </small>
              </div>

              {selectedFiles.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Selected Images ({selectedFiles.length})</label>
                  <div className="row">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="col-md-4 mb-2">
                        <div className="card">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                            className="card-img-top"
                            style={{ height: '150px', objectFit: 'cover' }}
                          />
                          <div className="card-body p-2">
                            <small className="text-muted d-block">
                              {file.name}
                            </small>
                            <small className="text-muted">
                              {formatFileSize(file.size)}
                            </small>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger float-end"
                              onClick={() => removeFile(index)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || selectedFiles.length === 0}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-upload me-2"></i>
                    Upload {selectedFiles.length} Image{selectedFiles.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload; 